import { motion, useScroll, useTransform } from 'motion/react';
import { useRef } from 'react';
import { useInView } from '../../../components/hooks/useInView.jsx';
import { MessageCircle, Brain, Activity, Heart, User } from 'lucide-react';

export function EmpathyMap() {
  const [ref, isInView] = useInView({ threshold: 0.1 });
  const containerRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"]
  });

  const rotate = useTransform(scrollYProgress, [0, 1], [0, 360]);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [0.8, 1, 0.8]);

  const quadrants = [
    {
      title: 'Says',
      icon: MessageCircle,
      color: '#ff6b9d',
      items: [
        '"I want quick and effective workouts I can fit into my day."',
        '"Meal suggestions need to match my dietary preferences."',
        '"I need clear progress tracking to stay motivated."',
        '"I love when I see tangible results in my fitness journey."'
      ],
      position: 'top-left'
    },
    {
      title: 'Thinks',
      icon: Brain,
      color: '#4facfe',
      items: [
        '"Am I balancing my fitness goals and personal life effectively?"',
        '"Is my current workout routine really working for me?"',
        '"I wish I could track my sleep and meals in one place."',
        '"How can I stay motivated without burning out?"'
      ],
      position: 'top-right'
    },
    {
      title: 'Does',
      icon: Activity,
      color: '#43e97b',
      items: [
        'Sets reminders for workouts but skips due to time constraints.',
        'Tracks meals inconsistently based on mood.',
        'Participates in challenges for motivation.',
        'Shares achievements with friends.'
      ],
      position: 'bottom-left'
    },
    {
      title: 'Feels',
      icon: Heart,
      color: '#fd5b38',
      items: [
        'Frustrated when progress is not measurable.',
        'Excited about fitness milestones.',
        'Anxious about consistency.',
        'Empowered by personalized insights.'
      ],
      position: 'bottom-right'
    }
  ];

  return (
    <section ref={containerRef} className="py-20 px-6 bg-gradient-to-b from-transparent via-black/40 to-transparent relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 opacity-10">
        <motion.div 
          style={{ rotate }}
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] item-border border-[#fd5b38]/30 rounded-full"
        />
        <motion.div 
          style={{ rotate: useTransform(rotate, (val) => -val) }}
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] item-border border-[#fd5b38]/20 rounded-full"
        />
      </div>

      <div ref={ref} className="max-w-7xl mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-5xl md:text-6xl mb-8">
            Empathy Map
          </h2>
          <div className="h-1 w-20 bg-[#fd5b38] mx-auto mb-8" />
          <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto mb-8">
            Deep dive into Sarah's perspective to understand her needs, motivations, and pain points
          </p>
          
          {/* Persona Badge */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="inline-flex items-center gap-3 bg-gradient-to-r from-[#fd5b38]/20 to-transparent px-6 py-3 rounded-full item-border border-[#fd5b38]/50"
          >
            <div className="w-12 h-12 bg-gradient-to-br from-[#fd5b38] to-[#ff7a5c] rounded-full flex items-center justify-center">
              <User className="w-6 h-6 text-white" />
            </div>
            <div className="text-left">
              <p className="text-sm text-gray-400">Persona</p>
              <p className="text-[#fd5b38]">Sarah, 28 • Marketing Assistant</p>
            </div>
          </motion.div>
        </motion.div>

        {/* Four Quadrants Grid */}
        <div className="grid md:grid-cols-2 gap-6 lg:gap-8 max-w-7xl mx-auto">
          {quadrants.map((quadrant, index) => {
            const delay = 0.5 + index * 0.15;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9, rotateY: -20 }}
                animate={isInView ? { opacity: 1, scale: 1, rotateY: 0 } : {}}
                transition={{ duration: 0.8, delay }}
                whileHover={{ 
                  scale: 1.03,
                  rotateY: 5,
                  boxShadow: `0 0 40px ${quadrant.color}40`
                }}
                className="group relative bg-gradient-to-br from-[#1a1a1a] to-black rounded-3xl item-border border-gray-800 hover:border-[#fd5b38] transition-all duration-500 overflow-hidden"
                style={{ perspective: '1000px' }}
              >
                {/* Glowing background on hover */}
                <div 
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-3xl"
                  style={{ background: `radial-gradient(circle at center, ${quadrant.color}20, transparent)` }}
                />

                {/* Gradient overlay */}
                <div 
                  className="absolute top-0 right-0 w-40 h-40 opacity-20 group-hover:opacity-30 transition-opacity duration-500 blur-3xl"
                  style={{ background: `radial-gradient(circle, ${quadrant.color}, transparent)` }}
                />

                <div className="relative z-10 p-8">
                  {/* Header */}
                  <div className="flex items-center gap-4 mb-6">
                    <motion.div 
                      whileHover={{ rotate: 360 }}
                      transition={{ duration: 0.6 }}
                      className="w-14 h-14 rounded-2xl flex items-center justify-center"
                      style={{ 
                        background: `linear-gradient(135deg, ${quadrant.color}30, ${quadrant.color}10)`,
                        border: `2px solid ${quadrant.color}50`
                      }}
                    >
                      <quadrant.icon 
                        className="w-7 h-7"
                        style={{ color: quadrant.color }}
                      />
                    </motion.div>
                    <h3 
                      className="text-3xl"
                      style={{ color: quadrant.color }}
                    >
                      {quadrant.title}
                    </h3>
                  </div>

                  {/* Content */}
                  <ul className="space-y-3">
                    {quadrant.items.map((item, itemIndex) => (
                      <motion.li
                        key={itemIndex}
                        initial={{ opacity: 0, x: -20 }}
                        animate={isInView ? { opacity: 1, x: 0 } : {}}
                        transition={{ duration: 0.6, delay: delay + 0.1 + itemIndex * 0.1 }}
                        className="flex items-start gap-3 text-gray-300 leading-relaxed group/item"
                      >
                        <span 
                          className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0 group-hover/item:scale-150 transition-transform duration-300"
                          style={{ backgroundColor: quadrant.color }}
                        />
                        <span className="group-hover/item:text-white transition-colors duration-300">
                          {item}
                        </span>
                      </motion.li>
                    ))}
                  </ul>
                </div>

                {/* Corner decoration */}
                <div 
                  className="absolute bottom-0 left-0 w-20 h-20 opacity-10"
                  style={{ 
                    background: `linear-gradient(45deg, ${quadrant.color}, transparent)`,
                    clipPath: 'polygon(0 100%, 100% 100%, 0 0)'
                  }}
                />
              </motion.div>
            );
          })}
        </div>

        {/* Center Circle - Desktop only */}
        <motion.div
          initial={{ opacity: 0, scale: 0 }}
          animate={isInView ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 0.8, delay: 1.2 }}
          style={{ scale }}
          className="hidden md:block absolute top-[46%] left-[46%] transform -translate-x-1/2 -translate-y-1/2 w-32 h-32 bg-gradient-to-br from-[#fd5b38] to-[#ff7a5c] rounded-full shadow-[0_0_60px_rgba(253,91,56,0.4)] z-20"
        >
          <div className="w-full h-full flex items-center justify-center">
            <div className="text-center">
              <User className="w-12 h-12 text-white mx-auto mb-1" />
              <p className="text-white text-xs">Sarah</p>
            </div>
          </div>
        </motion.div>

        {/* Key Insights */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 1.4 }}
          className="mt-16 bg-gradient-to-r from-transparent via-[#fd5b38]/10 to-transparent p-8 rounded-3xl item-border border-[#fd5b38]/30"
        >
          <h3 className="text-3xl md:text-4xl mb-6 text-center text-[#fd5b38]">
            Key Insights
          </h3>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            <div className="text-center">
              <div className="text-4xl mb-2">💭</div>
              <h4 className="text-xl mb-2 text-gray-200">Emotional Triggers</h4>
              <p className="text-gray-400">Sarah needs visual progress and quick wins to stay motivated and avoid burnout</p>
            </div>
            <div className="text-center">
              <div className="text-4xl mb-2">🎯</div>
              <h4 className="text-xl mb-2 text-gray-200">Core Need</h4>
              <p className="text-gray-400">An all-in-one solution that fits her busy schedule with personalized guidance</p>
            </div>
            <div className="text-center">
              <div className="text-4xl mb-2">⚡</div>
              <h4 className="text-xl mb-2 text-gray-200">Pain Point</h4>
              <p className="text-gray-400">Fragmented tools and inconsistent tracking make it hard to see real progress</p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
